import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { LogIn, AlertCircle, Lock } from "lucide-react";
import Layout from "../components/Layout";
import PasswordInput from "../components/PasswordInput";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import { rawSignIn } from "../lib/gotrueAuth";

export default function Login() {
  const { user, isSupabaseConfigured } = useCustomerAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const redirectTo = new URLSearchParams(loc.search).get("redirect") || "/os-meus-casos";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) nav(redirectTo);
  }, [user, nav, redirectTo]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const result = await rawSignIn(email.toLowerCase(), password);
      if (result.ok && result.session) {
        nav(redirectTo);
        return;
      }
      if (result.needsConfirmation) {
        setError("Email ainda não confirmado. Verifica a tua caixa de entrada.");
        return;
      }
      if (result.invalidCredentials) {
        setError("Email ou password incorretos.");
        return;
      }
      setError(result.error || "Não foi possível entrar.");
    } catch (err) {
      setError(err.message || "Não foi possível entrar.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <section className="min-h-[70vh] flex items-center py-20">
        <div className="max-w-md mx-auto px-6 w-full">
          <div className="dossier p-10">
            <div className="flex items-center gap-3 mb-2">
              <LogIn className="w-4 h-4 text-blood" />
              <p className="mono text-[10px] tracking-[0.3em] text-blood">ÁREA DE INVESTIGADORES</p>
            </div>
            <h1 className="headline text-3xl mb-2">Entrar</h1>
            <p className="text-bone-muted text-sm mb-8">
              Acede aos teus dossiers e descarrega os ficheiros confidenciais.
            </p>

            {!isSupabaseConfigured && (
              <div className="border border-blood/40 bg-blood/10 p-4 mb-6 flex gap-3">
                <AlertCircle className="w-4 h-4 text-blood shrink-0 mt-0.5" />
                <p className="mono text-[11px] text-bone-dim leading-relaxed">
                  Autenticação ainda não configurada. Configura SUPABASE_URL e SUPABASE_ANON_KEY no
                  ficheiro .env do frontend.
                </p>
              </div>
            )}

            <form onSubmit={submit} className="space-y-5">
              <Field label="Email">
                <input
                  data-testid="login-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="cec-input"
                  autoComplete="email"
                />
              </Field>
              <Field label="Password">
                <PasswordInput
                  testId="login-password"
                  value={password}
                  onChange={setPassword}
                  autoComplete="current-password"
                />
                <div className="flex justify-end mt-2">
                  <Link
                    to="/recuperar-password"
                    data-testid="login-forgot-password"
                    className="mono text-[10px] tracking-[0.25em] text-bone-muted hover:text-blood transition-colors"
                  >
                    Esqueceste a password?
                  </Link>
                </div>
              </Field>
              {error && (
                <p className="mono text-xs text-blood tracking-[0.15em]">{String(error)}</p>
              )}
              <button
                type="submit"
                disabled={busy || !isSupabaseConfigured}
                data-testid="login-submit"
                className="btn-primary w-full justify-center disabled:opacity-50"
              >
                {busy ? "A entrar..." : (<>Entrar <LogIn className="w-4 h-4" /></>)}
              </button>
            </form>

            <p className="mono text-[11px] text-bone-muted mt-8 text-center">
              Ainda não tens conta?{" "}
              <Link to="/criar-conta" className="text-blood hover:underline">
                Criar conta
              </Link>
            </p>
            <p className="mono text-[10px] text-bone-faded mt-4 text-center flex items-center justify-center gap-2">
              <Lock className="w-3 h-3" />
              <Link to="/admin/login" className="hover:text-bone-muted">Acesso de administrador</Link>
            </p>
          </div>
        </div>
      </section>
      <style>{`.cec-input{width:100%;background:#111;border:1px solid rgba(255,255,255,0.1);padding:12px 14px;font-family:'IBM Plex Mono',monospace;font-size:13px;color:#fff;letter-spacing:0.05em}.cec-input:focus{outline:none;border-color:#C8102E}`}</style>
    </Layout>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="mono text-[10px] tracking-[0.3em] text-bone-faded block mb-2">{label.toUpperCase()}</label>
    {children}
  </div>
);
